import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Pencil, Download, FileText, Loader2, CheckCircle2,
  Send, Clock, RotateCcw, Trash2, FileCheck, AlertTriangle, ExternalLink,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Link as LinkIcon } from 'lucide-react';
import { fmtDateIN, fmtINR } from '../components/invoicing/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtINR_localOverride_unused = (n) =>
  `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusStyle = (s) => {
  switch (s) {
    case 'paid': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'sent': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'overdue': return 'bg-red-100 text-red-700 border-red-200';
    case 'draft':
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const InvoiceViewPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);
  const [downloading, setDownloading] = useState('');

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, cfgRes] = await Promise.all([
        axios.get(`${API}/invoicing/invoices/${invoiceId}`),
        axios.get(`${API}/invoicing/config`).catch(() => ({ data: null })),
      ]);
      setInvoice(invRes.data);
      if (cfgRes.data) setConfig(cfgRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, navigate]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const updateStatus = async (newStatus) => {
    if (!invoice) return;
    if (newStatus === 'paid' && !window.confirm('Mark this invoice as PAID?')) return;
    setStatusBusy(true);
    try {
      await axios.post(`${API}/invoicing/invoices/${invoice.invoice_id}/status`, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status update failed');
    } finally {
      setStatusBusy(false);
    }
  };

  const download = async (format) => {
    setDownloading(format);
    try {
      const response = await axios.get(
        `${API}/invoicing/invoices/${invoice.invoice_id}/export?format=${format}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(invoice.invoice_number || 'invoice').replace(/\//g, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(`Failed to download ${format.toUpperCase()}`);
      console.error(err);
    } finally {
      setDownloading('');
    }
  };

  const remove = async () => {
    if (!invoice) return;
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/invoicing/invoices/${invoice.invoice_id}`);
      toast.success('Invoice deleted');
      navigate('/invoices');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const [issuingTax, setIssuingTax] = useState(false);
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, reason: '', cascade: true, submitting: false });

  // Edit invoice number dialog
  const [editNumDialog, setEditNumDialog] = useState({ open: false, newNumber: '', reason: '', submitting: false });

  const openEditNumber = () => {
    setEditNumDialog({ open: true, newNumber: invoice.invoice_number, reason: '', submitting: false });
  };

  const submitEditNumber = async () => {
    const { newNumber, reason } = editNumDialog;
    if (!newNumber || !newNumber.trim()) {
      toast.error('New invoice number is required');
      return;
    }
    if (newNumber.trim() === invoice.invoice_number) {
      toast.error('New invoice number must differ from the current one');
      return;
    }
    if (!reason || reason.trim().length < 5) {
      toast.error('Please enter a reason (at least 5 characters) — required for audit trail.');
      return;
    }
    setEditNumDialog((p) => ({ ...p, submitting: true }));
    try {
      await axios.post(`${API}/invoicing/invoices/${invoice.invoice_id}/edit-number`,
        { invoice_number: newNumber.trim(), reason: reason.trim() });
      toast.success(`Invoice number updated to ${newNumber.trim()}`);
      setEditNumDialog({ open: false, newNumber: '', reason: '', submitting: false });
      fetchInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit failed');
      setEditNumDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const submitHardDelete = async () => {
    if (!hardDeleteDialog.reason || hardDeleteDialog.reason.trim().length < 5) {
      toast.error('Please enter a reason (at least 5 characters) — required for audit trail.');
      return;
    }
    setHardDeleteDialog((p) => ({ ...p, submitting: true }));
    try {
      const { data } = await axios.post(
        `${API}/invoicing/invoices/${invoice.invoice_id}/hard-delete`,
        { reason: hardDeleteDialog.reason.trim(), cascade: hardDeleteDialog.cascade },
      );
      toast.success(`Hard-deleted ${data.deleted_ids.length} document${data.deleted_ids.length !== 1 ? 's' : ''}`);
      navigate('/invoices');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hard delete failed');
      setHardDeleteDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const issueTaxInvoice = async () => {
    if (!invoice) return;
    if (!window.confirm(
      `Issue Tax Invoice from proforma ${invoice.invoice_number}?\n\n` +
      `This will:\n` +
      `• Create a new GST-compliant Tax Invoice with its own AKSTLC sequence number\n` +
      `• Lock this Proforma from further edits or deletion\n` +
      `• Make the Tax Invoice immutable (GST audit trail)\n\n` +
      `Continue?`
    )) return;
    setIssuingTax(true);
    try {
      const { data } = await axios.post(`${API}/invoicing/invoices/${invoice.invoice_id}/issue-tax-invoice`);
      toast.success(`Tax Invoice ${data.invoice_number} issued`);
      navigate(`/invoices/${data.invoice_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue tax invoice');
    } finally {
      setIssuingTax(false);
    }
  };

  if (loading || !invoice) {
    return (
      <div className="min-h-screen bg-[#FDF8F0]">
        <AppSidebar user={user} onLogout={logout} active="invoices" />
        <main className="ml-72 p-8 flex items-center justify-center h-screen">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading invoice...
        </main>
      </div>
    );
  }

  const client = invoice.client_snapshot || {};
  const status = invoice.status || 'draft';
  const org = config?.org;
  const bank = org?.bank;
  const isProforma = (invoice.doc_type || 'proforma') === 'proforma';
  const isTaxInvoice = invoice.doc_type === 'tax_invoice';
  const hasLinkedTax = !!invoice.linked_tax_invoice_id;
  const canEdit = isProforma && status !== 'paid' && !hasLinkedTax;
  const canDelete = isProforma && !hasLinkedTax;
  const canIssueTax = isProforma && status === 'paid' && !hasLinkedTax;

  // Theme: proforma uses slate, tax invoice uses gold/ink
  const theme = isProforma
    ? {
        bg: 'bg-slate-50',
        accent: '#475569',
        accentBg: 'bg-slate-200',
        accentText: 'text-slate-700',
        accentBorder: 'border-slate-300',
        title: 'PROFORMA INVOICE',
        titleColor: 'text-slate-700',
        stripBg: 'bg-slate-700',
        stripAccent: 'text-slate-200',
        modeBadge: 'bg-slate-100 text-slate-700 border-slate-200',
      }
    : {
        bg: 'bg-[#FDF8F0]',
        accent: '#C9A84C',
        accentBg: 'bg-[#FDF8F0]',
        accentText: 'text-[#8B7220]',
        accentBorder: 'border-[#E0D8CC]',
        title: 'TAX INVOICE',
        titleColor: 'text-[#F59E0B]',
        stripBg: 'bg-[#1A1A2E]',
        stripAccent: 'text-[#E8C97A]',
        modeBadge: 'bg-[#C9A84C]/15 text-[#8B7220] border-[#C9A84C]/30',
      };

  return (
    <div className={`min-h-screen ${theme.bg}`} style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <AppSidebar user={user} onLogout={logout} active="invoices" />

      <main className="ml-72 p-8" data-testid="invoice-view-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <button
              onClick={() => navigate('/invoices')}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-2"
              data-testid="back-to-invoices"
            >
              <ArrowLeft className="w-4 h-4" /> Back to invoices
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-[#1A1A2E] font-mono">{invoice.invoice_number}</h1>
              <button
                onClick={openEditNumber}
                className="text-xs text-slate-500 hover:text-[#1A1A2E] hover:underline flex items-center gap-1"
                title="Edit invoice number (audit-logged)"
                data-testid="edit-invoice-number-btn"
              >
                <Pencil className="w-3 h-3" /> edit #
              </button>
              <Badge
                className={`${isProforma ? 'bg-slate-200 text-slate-800 border-slate-300' : 'bg-[#1A1A2E] text-[#E8C97A] border-[#1A1A2E]'} border text-xs uppercase tracking-wide font-semibold`}
                data-testid="doc-type-badge"
              >
                {isProforma ? 'Proforma' : 'Tax Invoice'}
              </Badge>
              <Badge className={`${statusStyle(status)} border capitalize text-sm`} data-testid="invoice-status-badge">
                {status}
              </Badge>
              <Badge className={`${theme.modeBadge} border text-xs`} data-testid="tax-mode-badge">
                {invoice.tax_mode === 'inter' ? 'Inter-state · IGST' : 'Intra-state · CGST+SGST'}
              </Badge>
            </div>

            {/* Linked document indicator */}
            {invoice.linked_tax_invoice_number && (
              <button
                onClick={() => navigate(`/invoices/${invoice.linked_tax_invoice_id}`)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-800 hover:underline font-mono"
                data-testid="linked-tax-invoice-link"
              >
                <ExternalLink className="w-3 h-3" />
                Tax Invoice issued: {invoice.linked_tax_invoice_number}
              </button>
            )}
            {invoice.source_proforma_number && (
              <button
                onClick={() => navigate(`/invoices/${invoice.source_proforma_id}`)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 hover:underline font-mono"
                data-testid="source-proforma-link"
              >
                <ExternalLink className="w-3 h-3" />
                Issued against Proforma: {invoice.source_proforma_number}
              </button>
            )}

            <p className="text-sm text-slate-500 mt-2">
              Created {fmtDateIN(invoice.created_at?.slice(0, 10))} · Last updated {fmtDateIN(invoice.updated_at?.slice(0, 10))}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {canIssueTax && (
              <Button onClick={issueTaxInvoice} disabled={issuingTax}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
                      data-testid="issue-tax-invoice-btn">
                {issuingTax ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck className="w-4 h-4 mr-2" />}
                Issue Tax Invoice
              </Button>
            )}
            <Button variant="outline" onClick={() => download('pdf')} disabled={downloading === 'pdf'}
                    data-testid="download-pdf-btn">
              {downloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              PDF
            </Button>
            <Button variant="outline" onClick={() => download('docx')} disabled={downloading === 'docx'}
                    data-testid="download-docx-btn">
              {downloading === 'docx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Word
            </Button>
            {canEdit && (
              <Button variant="outline" onClick={() => navigate(`/invoices/${invoice.invoice_id}/edit`)}
                      data-testid="edit-invoice-btn">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" onClick={remove}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="delete-invoice-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            {/* Hard Delete: gated for PAID tax invoices — only super_admin can perform it.
                Regular admins see a disabled button with an explanatory tooltip. */}
            {(() => {
              const isPaidTaxInvoice = invoice.doc_type === 'tax_invoice' && invoice.status === 'paid';
              const isSuperAdmin = user?.role === 'super_admin';
              const blockedForRole = isPaidTaxInvoice && !isSuperAdmin;
              return (
                <Button
                  variant="outline"
                  onClick={() => setHardDeleteDialog({ open: true, reason: '', cascade: true, submitting: false })}
                  className={blockedForRole
                    ? 'text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                    : 'text-red-700 border-red-300 hover:bg-red-100'}
                  disabled={blockedForRole}
                  title={blockedForRole
                    ? 'Only a Super Admin can hard-delete a PAID tax invoice (GST audit-trail safety).'
                    : 'Force delete with audit trail — bypasses GST immutability'}
                  data-testid="hard-delete-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" /> Hard Delete
                </Button>
              );
            })()}
          </div>
        </div>

        {/* Proforma disclaimer banner */}
        {isProforma && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-300 rounded-lg p-4 flex items-start gap-3"
               data-testid="proforma-disclaimer">
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-0.5">This is a Proforma Invoice — NOT a Tax Invoice.</p>
              <p className="text-xs text-amber-800 leading-relaxed">
                No Input Tax Credit can be claimed under GST against this document.
                {canIssueTax && ' Once payment is received and confirmed, click "Issue Tax Invoice" above to generate the GST-compliant Tax Invoice.'}
                {!canIssueTax && status !== 'paid' && ' A Tax Invoice will be issued upon marking this Proforma as paid.'}
              </p>
            </div>
          </div>
        )}

        {/* Tax Invoice immutability notice */}
        {isTaxInvoice && (
          <div className="mb-6 bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 flex items-start gap-3"
               data-testid="tax-invoice-notice">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <p className="font-semibold mb-0.5">GST-Compliant Tax Invoice</p>
              <p className="text-xs text-emerald-800 leading-relaxed">
                This Tax Invoice is immutable by GST law. It cannot be edited or deleted.
                Any corrections require a Credit Note or Debit Note to be issued separately.
              </p>
            </div>
          </div>
        )}

        {/* Status action bar — proforma only */}
        {isProforma && !hasLinkedTax && (
        <Card className="mb-6 border-[#E0D8CC]">
          <CardContent className="py-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-slate-600 mr-2">Update status:</span>
            <Button size="sm" variant={status === 'draft' ? 'default' : 'outline'} disabled={statusBusy || status === 'draft'}
                    onClick={() => updateStatus('draft')} data-testid="status-draft-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Draft
            </Button>
            <Button size="sm" variant={status === 'sent' ? 'default' : 'outline'} disabled={statusBusy || status === 'sent'}
                    onClick={() => updateStatus('sent')} data-testid="status-sent-btn"
                    className={status === 'sent' ? 'bg-blue-600 hover:bg-blue-700' : ''}>
              <Send className="w-3.5 h-3.5 mr-1.5" /> Mark Sent
            </Button>
            <Button size="sm" variant={status === 'paid' ? 'default' : 'outline'} disabled={statusBusy || status === 'paid'}
                    onClick={() => updateStatus('paid')} data-testid="status-paid-btn"
                    className={status === 'paid' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
            </Button>
            <Button size="sm" variant={status === 'overdue' ? 'default' : 'outline'} disabled={statusBusy || status === 'overdue'}
                    onClick={() => updateStatus('overdue')} data-testid="status-overdue-btn"
                    className={status === 'overdue' ? 'bg-red-600 hover:bg-red-700' : ''}>
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Mark Overdue
            </Button>
            {invoice.paid_at && (
              <span className="text-xs text-emerald-700 ml-2">
                Paid on {fmtDateIN(invoice.paid_at?.slice(0, 10))}
              </span>
            )}
          </CardContent>
        </Card>
        )}

        {/* ==================== INVOICE PAPER ==================== */}
        <div
          className={`bg-white border ${isProforma ? 'border-slate-300' : 'border-[#E0D8CC]'} rounded-md mx-auto shadow-md relative overflow-hidden`}
          style={{ maxWidth: '820px' }}
          data-testid="invoice-paper"
        >
          {/* PROFORMA watermark */}
          {isProforma && (
            <div
              aria-hidden
              className="pointer-events-none select-none absolute inset-0 flex items-center justify-center"
              style={{ zIndex: 0 }}
            >
              <span
                className="text-slate-200 font-bold tracking-[0.2em]"
                style={{ fontSize: 140, transform: 'rotate(-30deg)', opacity: 0.35 }}
              >
                PROFORMA
              </span>
            </div>
          )}
          <div className="relative" style={{ zIndex: 1 }}>
          {/* HEADER */}
          <div className={`grid grid-cols-1 md:grid-cols-[1.6fr,1fr] gap-8 p-10 border-b-[1.5px] ${isProforma ? 'border-slate-300' : 'border-[#E0D8CC]'}`}>
            <div>
              {/* Logo + firm name on the same row; subsequent lines span full width below */}
              <div className="flex items-center gap-4 mb-2">
                <img
                  src="/aks-logo.png"
                  alt="AKS Logo"
                  className="object-contain flex-shrink-0"
                  style={{ height: '52px', width: 'auto' }}
                  data-testid="invoice-logo"
                />
                <h2
                  className="text-[#1A1A2E] min-w-0"
                  style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontWeight: 600, fontSize: 24, lineHeight: 1.1 }}
                >
                  {org?.name || 'AKS Tax and Legal Consultancy'}
                </h2>
              </div>
              <p className={`text-[11.5px] italic mb-2 tracking-wide ${isProforma ? 'text-slate-600' : 'text-[#C9A84C]'}`}>
                {org?.tagline || 'Advisory & Consultancy · GST · Customs · FEMA · PMLA'}
              </p>
              <div className="text-[11px] text-[#5A5A7A] leading-[1.6]">
                {(org?.address_lines || []).map((l, i) => <div key={i}>{l}</div>)}
                {org?.email && <div>{org.email}</div>}
              </div>
            </div>
            <div className="text-right space-y-1">
              <p
                className={isProforma ? 'text-slate-700 tracking-wide' : 'text-[#1A1A2E] tracking-wide'}
                style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontWeight: 400, fontSize: isProforma ? 30 : 36, lineHeight: 1 }}
                data-testid="paper-title"
              >
                {isProforma ? 'PROFORMA INVOICE' : 'TAX INVOICE'}
              </p>
              <div className="pt-2 space-y-0.5 text-right">
                <div className="text-[9px] uppercase tracking-[2px] text-[#5A5A7A]">PAN</div>
                <div className="font-mono text-sm font-semibold text-[#1A1A2E]">{org?.pan || 'ANOPS4530N'}</div>
                <div className="text-[9px] uppercase tracking-[2px] text-[#5A5A7A] pt-1">GSTIN</div>
                <div className="font-mono text-sm font-semibold text-[#1A1A2E]">{org?.gstin || '22ANOPS4530N1ZK'}</div>
              </div>
            </div>
          </div>

          {/* STRIP — Invoice no / date / Place of supply */}
          <div className={`grid grid-cols-1 md:grid-cols-3 ${isProforma ? 'bg-slate-700' : 'bg-[#1A1A2E]'} text-white px-10 py-3`}>
            <div>
              <div className="text-[9px] uppercase tracking-[2px] text-white/50 mb-0.5">
                {isProforma ? 'Proforma No.' : 'Invoice No.'}
              </div>
              <div className={`font-semibold text-sm ${isProforma ? 'text-slate-100' : 'text-[#E8C97A]'}`}>{invoice.invoice_number}</div>
            </div>
            <div className="md:text-center">
              <div className="text-[9px] uppercase tracking-[2px] text-white/50 mb-0.5">Date</div>
              <div className={`font-semibold text-sm ${isProforma ? 'text-slate-100' : 'text-[#E8C97A]'}`}>{fmtDateIN(invoice.issue_date)}</div>
            </div>
            <div className="md:text-right">
              <div className="text-[9px] uppercase tracking-[2px] text-white/50 mb-0.5">Place of Supply</div>
              <div className={`font-semibold text-sm ${isProforma ? 'text-slate-100' : 'text-[#E8C97A]'}`}>{invoice.place_of_supply || '—'}</div>
            </div>
          </div>

          {/* BILL TO */}
          <div className={`px-10 py-6 border-b-[1.5px] ${isProforma ? 'border-slate-300' : 'border-[#E0D8CC]'}`}>
            <div className={`text-[9px] uppercase tracking-[2px] font-semibold mb-2 ${isProforma ? 'text-slate-600' : 'text-[#C9A84C]'}`}>Bill To</div>
            <p className="text-base font-semibold text-[#1A1A2E] mb-1">{client.name || '—'}</p>
            {client.address && <p className="text-[11.5px] text-[#5A5A7A] whitespace-pre-line leading-[1.7]">{client.address}</p>}
            {client.state && <p className="text-[11.5px] text-[#5A5A7A]">{client.state}</p>}
            {client.email && <p className="text-[11.5px] text-[#5A5A7A] mt-1">{client.email}</p>}
            {client.gstin && (
              <p className={`mt-2 inline-block font-mono text-[11px] px-2 py-1 rounded ${isProforma ? 'bg-slate-200 text-slate-700' : 'bg-[#C9A84C]/15 text-[#8B7220]'}`}>
                GSTIN: {client.gstin}
              </p>
            )}
          </div>

          {/* LINE ITEMS */}
          <div className="px-10 pt-6 pb-2">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#FDF8F0] border-b-[1.5px] border-[#E0D8CC]">
                  <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider font-semibold text-[#1A1A2E] w-10">#</th>
                  <th className="px-3 py-3 text-left text-[10px] uppercase tracking-wider font-semibold text-[#1A1A2E]">Service Description</th>
                  <th className="px-3 py-3 text-center text-[10px] uppercase tracking-wider font-semibold text-[#1A1A2E] w-20">SAC</th>
                  <th className="px-3 py-3 text-right text-[10px] uppercase tracking-wider font-semibold text-[#1A1A2E] w-32">Amount (INR)</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.line_items || []).map((li, idx) => (
                  <tr key={idx} className="border-b border-[#E0D8CC]/60" data-testid={`view-line-item-${idx}`}>
                    <td className="px-3 py-3 text-[#5A5A7A] align-top">{idx + 1}</td>
                    <td className="px-3 py-3 text-[#1A1A2E] whitespace-pre-wrap break-words align-top">{li.description}</td>
                    <td className="px-3 py-3 text-center font-mono text-[12px] text-[#5A5A7A] align-top">{li.sac_code || '—'}</td>
                    <td className="px-3 py-3 text-right text-[#1A1A2E] font-medium align-top whitespace-nowrap">
                      {fmtINR(li.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTALS */}
          <div className="px-10 pb-6 flex justify-end">
            <div className="w-full max-w-sm border-t border-[#E0D8CC] pt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#5A5A7A]">Subtotal</span>
                <span className="font-medium text-[#1A1A2E]" data-testid="view-subtotal">{fmtINR(invoice.subtotal)}</span>
              </div>
              {invoice.tax_mode === 'intra' ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A7A]">CGST @ {invoice.cgst_percent}%</span>
                    <span className="text-[#1A1A2E]" data-testid="view-cgst">{fmtINR(invoice.cgst_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5A5A7A]">SGST @ {invoice.sgst_percent}%</span>
                    <span className="text-[#1A1A2E]" data-testid="view-sgst">{fmtINR(invoice.sgst_amount)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-[#5A5A7A]">IGST @ {invoice.igst_percent}%</span>
                  <span className="text-[#1A1A2E]" data-testid="view-igst">{fmtINR(invoice.igst_amount)}</span>
                </div>
              )}
              {invoice.cess_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#5A5A7A]">Cess @ {invoice.cess_percent}%</span>
                  <span className="text-[#1A1A2E]" data-testid="view-cess">{fmtINR(invoice.cess_amount)}</span>
                </div>
              )}
              <div className="flex justify-between bg-[#FDF8F0] -mx-3 px-3 py-2 border-y-2 border-[#1A1A2E] mt-3">
                <span className="text-[14px] font-bold text-[#1A1A2E]">Total (INR)</span>
                <span className="text-lg font-bold text-[#1A1A2E]" data-testid="view-total">
                  {fmtINR(invoice.total)}
                </span>
              </div>
            </div>
          </div>

          {/* AMOUNT IN WORDS */}
          {invoice.amount_in_words && (
            <div className={`mx-10 mb-6 px-3 py-2 border rounded text-[11.5px] text-[#5A5A7A] ${isProforma ? 'bg-slate-50 border-slate-300' : 'bg-[#FAF7F2] border-[#E0D8CC]'}`}>
              <span className="font-semibold text-[#1A1A2E]">Amount in Words:</span> {invoice.amount_in_words}
            </div>
          )}

          {/* FOOTER: Bank | Signatory */}
          <div className={`grid grid-cols-1 md:grid-cols-2 border-t-[1.5px] ${isProforma ? 'border-slate-300' : 'border-[#E0D8CC]'}`}>
            <div className={`px-10 py-5 md:border-r-[1.5px] ${isProforma ? 'border-slate-300' : 'border-[#E0D8CC]'}`}>
              <div className={`text-[9px] uppercase tracking-[2px] font-semibold mb-2 ${isProforma ? 'text-slate-600' : 'text-[#C9A84C]'}`}>Bank Details</div>
              <div className="text-[11.5px] text-[#5A5A7A] leading-[1.75]">
                <p className="font-semibold text-[#1A1A2E]">{bank?.name || 'IDBI Bank, Bhilai – 490020'}</p>
                <p>A/c Holder: {bank?.account_holder || 'AKS TAX AND LEGAL CONSULTANCY'}</p>
                <p>A/c No: <span className="font-mono text-[10.5px] text-[#1A1A2E]">{bank?.account_no || '0048102000031833'}</span></p>
                <p>IFSC: <span className="font-mono text-[10.5px] text-[#1A1A2E]">{bank?.ifsc || 'IBKL0000048'}</span></p>
              </div>
            </div>
            <div className="px-10 py-5 text-right">
              <div className={`text-[9px] uppercase tracking-[2px] font-semibold mb-2 ${isProforma ? 'text-slate-600' : 'text-[#C9A84C]'}`}>
                {isProforma ? 'For Approval' : 'Authorised Signatory'}
              </div>
              <div className="flex items-end justify-end gap-3 mb-1">
                <img src="/aks-seal.png" alt="AKS Seal"
                     className="w-20 h-20 object-contain"
                     data-testid="invoice-seal" />
                <img src="/aks-signature.png" alt="Authorised Signature"
                     className="h-20 w-auto object-contain"
                     data-testid="invoice-signature" />
              </div>
              <p
                className="text-[#1A1A2E]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 16, lineHeight: 1.2 }}
              >
                {org?.proprietor || 'Ashok Kumar Singh'}
              </p>
              <p className="text-[11.5px] text-[#5A5A7A]">Proprietor, {org?.name || 'AKS Tax and Legal Consultancy'}</p>
            </div>
          </div>

          {/* TERMS */}
          {invoice.terms && (
            <div className={`px-10 py-4 border-t ${isProforma ? 'bg-slate-50 border-slate-300' : 'bg-[#FAF7F2] border-[#E0D8CC]'}`}>
              <div className={`text-[9px] uppercase tracking-[2px] font-semibold mb-1.5 ${isProforma ? 'text-slate-600' : 'text-[#C9A84C]'}`}>Terms &amp; Conditions</div>
              <p className="text-[11.5px] text-[#5A5A7A] whitespace-pre-line">{invoice.terms}</p>
            </div>
          )}

          {/* BOTTOM */}
          <div className={`px-10 py-3 text-center text-[10px] tracking-wide ${isProforma ? 'bg-slate-700 text-white/60' : 'bg-[#1A1A2E] text-white/60'}`}>
            {isProforma
              ? <>This is a <b>PROFORMA INVOICE</b> for client approval — NOT a Tax Invoice. No Input Tax Credit can be claimed under GST.</>
              : <>This is a computer-generated TAX INVOICE issued under GST.</>}
            &nbsp;&nbsp;·&nbsp;&nbsp;
            <span className="font-mono">GSTIN: {org?.gstin || '22ANOPS4530N1ZK'}</span>
            &nbsp;&nbsp;·&nbsp;&nbsp;
            <span className="font-mono">PAN: {org?.pan || 'ANOPS4530N'}</span>
          </div>
          </div>{/* end relative wrapper */}
        </div>

        {/* Hard Delete Dialog */}
        <Dialog open={hardDeleteDialog.open} onOpenChange={(o) => !o && setHardDeleteDialog((p) => ({ ...p, open: false }))}>
          <DialogContent className="max-w-lg" data-testid="hard-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Hard Delete {isProforma ? 'Proforma' : 'Tax Invoice'}
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                This <strong>permanently deletes</strong> {invoice.invoice_number} — bypassing the GST immutability guard.
                The deletion will be recorded in the audit log with your reason.
              </DialogDescription>
            </DialogHeader>

            {invoice.doc_type === 'tax_invoice' && invoice.status === 'paid' && (
              <div
                className="bg-purple-50 border-2 border-purple-200 rounded p-3 text-sm flex items-start gap-2"
                data-testid="super-admin-notice"
              >
                <AlertTriangle className="w-4 h-4 text-purple-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-purple-900">Super Admin action</p>
                  <p className="text-purple-800 text-xs mt-0.5">
                    You are deleting a <strong>paid tax invoice</strong>. This is a privileged operation
                    restricted to Super Admins under GST audit-trail safety norms. Your role
                    (super_admin) and a full snapshot of this document will be recorded in the
                    audit log alongside your reason.
                  </p>
                </div>
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm space-y-1">
              <div className="font-mono text-[13px] font-semibold text-slate-900">{invoice.invoice_number}</div>
              <div className="text-xs text-slate-500">
                {invoice.client_snapshot?.name} · ₹ {Number(invoice.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} · {status}
              </div>
              {(invoice.linked_tax_invoice_number || invoice.source_proforma_number) && (
                <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <LinkIcon className="w-3 h-3" />
                  Linked to: {invoice.linked_tax_invoice_number || invoice.source_proforma_number}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="hd-reason" className="text-sm font-medium">
                Reason <span className="text-red-500">*</span>
                <span className="text-xs text-slate-400 ml-1">(min 5 chars, stored in audit log)</span>
              </Label>
              <Textarea
                id="hd-reason"
                value={hardDeleteDialog.reason}
                onChange={(e) => setHardDeleteDialog((p) => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Test invoice from initial setup — cleanup before go-live"
                rows={2}
                className="mt-1"
                data-testid="hard-delete-reason-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">{hardDeleteDialog.reason.trim().length} characters</p>
            </div>

            {(invoice.linked_tax_invoice_id || invoice.source_proforma_id) && (
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hardDeleteDialog.cascade}
                  onChange={(e) => setHardDeleteDialog((p) => ({ ...p, cascade: e.target.checked }))}
                  className="mt-1 w-3.5 h-3.5 accent-red-600"
                  data-testid="hard-delete-cascade-checkbox"
                />
                <span>
                  <span className="font-medium">Also delete linked counterpart</span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {isProforma
                      ? 'If checked, the issued Tax Invoice will also be deleted.'
                      : 'If checked, the source Proforma will also be deleted.'}
                    {' '}Unchecked = counterpart kept but unlinked.
                  </span>
                </span>
              </label>
            )}

            <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>This action cannot be undone.</strong> Use only for test data cleanup or genuine errors. For corrections to legitimate tax invoices, issue a Credit Note instead.
              </span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline"
                      onClick={() => setHardDeleteDialog((p) => ({ ...p, open: false }))}
                      disabled={hardDeleteDialog.submitting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitHardDelete}
                disabled={hardDeleteDialog.submitting || hardDeleteDialog.reason.trim().length < 5}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="hard-delete-confirm-btn"
              >
                {hardDeleteDialog.submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                  : <><AlertTriangle className="w-4 h-4 mr-2" />Hard Delete Permanently</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Number Dialog */}
        <Dialog open={editNumDialog.open} onOpenChange={(o) => !o && setEditNumDialog((p) => ({ ...p, open: false }))}>
          <DialogContent className="max-w-lg" data-testid="edit-number-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#1A1A2E]">
                <Pencil className="w-5 h-5" />
                Edit Invoice Number
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Change the invoice number on this document. The change will be logged with your reason for audit purposes.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
              <div className="text-xs text-slate-500 mb-1">Current number:</div>
              <div className="font-mono text-base font-semibold text-slate-900">{invoice.invoice_number}</div>
            </div>

            <div>
              <Label htmlFor="new-number" className="text-sm font-medium">
                New Invoice Number <span className="text-red-500">*</span>
              </Label>
              <input
                id="new-number"
                type="text"
                value={editNumDialog.newNumber}
                onChange={(e) => setEditNumDialog((p) => ({ ...p, newNumber: e.target.value }))}
                className="mt-1 w-full font-mono text-sm px-3 py-2 border border-slate-300 rounded-md focus:border-[#1A1A2E] outline-none"
                data-testid="edit-number-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">Must be unique across all invoices.</p>
            </div>

            <div>
              <Label htmlFor="edit-reason" className="text-sm font-medium">
                Reason <span className="text-red-500">*</span>
                <span className="text-xs text-slate-400 ml-1">(min 5 chars, stored in audit log)</span>
              </Label>
              <Textarea
                id="edit-reason"
                value={editNumDialog.reason}
                onChange={(e) => setEditNumDialog((p) => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Sequence correction after numbering mistake"
                rows={2}
                className="mt-1"
                data-testid="edit-number-reason-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">{editNumDialog.reason.trim().length} characters</p>
            </div>

            {invoice.number_edits?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
                <p className="font-semibold text-amber-900 mb-1.5">Previous edits ({invoice.number_edits.length}):</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {invoice.number_edits.map((e, i) => (
                    <div key={i} className="text-amber-800 font-mono">
                      {fmtDateIN(e.edited_at?.slice(0, 10))}: <span className="line-through">{e.old_number}</span> → <span>{e.new_number}</span>
                      <div className="text-[10px] text-amber-700 italic ml-3">"{e.reason}"</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline"
                      onClick={() => setEditNumDialog((p) => ({ ...p, open: false }))}
                      disabled={editNumDialog.submitting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={submitEditNumber}
                disabled={editNumDialog.submitting || !editNumDialog.newNumber.trim() || editNumDialog.reason.trim().length < 5}
                className="bg-[#1A1A2E] hover:bg-[#2E2E4A] text-white"
                data-testid="edit-number-confirm-btn"
              >
                {editNumDialog.submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  : <><Pencil className="w-4 h-4 mr-2" />Save Number</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default InvoiceViewPage;
